import { useCallback, useEffect, useMemo, useState } from 'react';
import { SettingsFlow, UiNode } from '@ory/client';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import {
  isOryApiError,
  rewriteActionUrl,
  getCsrfTokenFromFlow,
  getOidcNodesByAction,
} from 'src/auth/utils/ory-helpers';

interface InviteIdentityTraits {
  email?: string;
}

interface InviteIdentityMetadata {
  firstName?: string;
  inviterName?: string;
  tenantId?: string;
}

interface UseInviteSettingsFlowReturn {
  flow: SettingsFlow | undefined;
  isLoading: boolean;
  error: string | null;
  userEmail: string;
  firstName: string;
  inviterName: string;
  tenantName: string;
  oidcNodes: UiNode[];
  csrfToken: string;
  formAction: string;
  updatePassword: (password: string) => Promise<{ success: boolean }>;
  clearError: () => void;
}

export const useInviteSettingsFlow = (flowId: string): UseInviteSettingsFlowReturn => {
  const [flow, setFlow] = useState<SettingsFlow>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initFlow = async () => {
      try {
        const { data } = await ory.getSettingsFlow({ id: flowId });
        if (!cancelled) setFlow(data);
      } catch (err: unknown) {
        if (cancelled) return;
        Logger.error('Failed to initialize settings flow', err);

        if (isOryApiError(err) && err.response?.status === 401) {
          setError(
            'Your invite link may have expired or is invalid. Please contact your administrator for a new invite.',
          );
        } else {
          setError('Failed to initialize. Please try refreshing the page.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    initFlow();

    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const identity = flow?.identity;
  const traits = identity?.traits as InviteIdentityTraits | undefined;
  const metadata = identity?.metadata_public as InviteIdentityMetadata | undefined;

  const userEmail = traits?.email ?? '';
  const firstName = metadata?.firstName ?? '';
  const inviterName = metadata?.inviterName ?? '';
  const tenantName = metadata?.tenantId ?? '';

  const oidcNodes = useMemo(() => getOidcNodesByAction(flow, ['link']), [flow]);

  const csrfToken = useMemo(() => getCsrfTokenFromFlow(flow), [flow]);

  const formAction = useMemo(() => rewriteActionUrl(flow?.ui.action ?? ''), [flow]);

  const updatePassword = useCallback(
    async (password: string): Promise<{ success: boolean }> => {
      if (!flow) {
        setError('Settings flow not initialized. Please refresh the page.');
        return { success: false };
      }

      setError(null);

      try {
        const { data } = await ory.updateSettingsFlow({
          flow: flow.id,
          updateSettingsFlowBody: {
            method: 'password',
            password,
            csrf_token: csrfToken,
          },
        });

        if (data.state === 'success') {
          return { success: true };
        }

        setFlow(data);
        const messages = data.ui.messages ?? [];
        const errorMessages = messages.filter(m => m.type === 'error');
        if (errorMessages.length > 0) {
          setError(errorMessages.map(m => m.text).join(' '));
        }
        return { success: false };
      } catch (err: unknown) {
        Logger.error('Failed to update password', err);

        if (!isOryApiError(err)) {
          setError('An unexpected error occurred. Please try again.');
          return { success: false };
        }

        const errorData = err.response?.data as SettingsFlow | undefined;
        if (errorData?.ui?.messages) {
          const messages = errorData.ui.messages;
          const errorMessages = messages?.filter((m: { type: string }) => m.type === 'error') ?? [];
          if (errorMessages.length > 0) {
            setError(errorMessages.map((m: { text: string }) => m.text).join(' '));
          } else {
            setError('Failed to set password. Please try again.');
          }
          setFlow(errorData);
        } else if (err.response?.status === 400) {
          setError(
            'The password does not meet the security requirements. Please try a different password.',
          );
        } else if (err.response?.status === 401) {
          setError('Your session has expired. Please refresh the page and try again.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        return { success: false };
      }
    },
    [flow, csrfToken],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    flow,
    isLoading,
    error,
    userEmail,
    firstName,
    inviterName,
    tenantName,
    oidcNodes,
    csrfToken,
    formAction,
    updatePassword,
    clearError,
  };
};
